import { useMemo } from 'react';

import type {
  Appointment,
  TTreatment,
} from '../../../react-query/useAppointments';
import {
  type Task,
  useGetTaskDisplayName,
} from '../../../react-query/useGetTasks';
import { usePreferences } from '../../../react-query/usePreferences';
import { sortByTooth } from '../../annotator/casePresentation/helpers';

export interface AggregatedTreatment {
  toothNum: string;
  treatments: TTreatment[];
}

export const useTreatmentsByToothNumber = (
  treatments?: TTreatment[],
): AggregatedTreatment[] => {
  return useMemo(() => {
    if (!treatments) return [];

    const toothNumbersToTreatments = treatments.reduce(
      (acc, treatment) => {
        if (!treatment.toothNum) return acc;

        const currTreatments = acc[treatment.toothNum];
        if (currTreatments) {
          acc[treatment.toothNum] = {
            ...currTreatments,
            treatments: [...currTreatments.treatments, treatment],
          };
        } else {
          acc[treatment.toothNum] = {
            toothNum: treatment.toothNum,
            treatments: [treatment],
          };
        }
        return acc;
      },
      {} as Record<string, AggregatedTreatment>,
    );

    return Object.values(toothNumbersToTreatments).sort((a, b) =>
      sortByTooth(a.toothNum, b.toothNum),
    );
  }, [treatments]);
};

interface DisplayAppointmentTask {
  label: string;
  lastTreatmentDate?: Date | string | null;
}

export const useDisplayTasks = (appointment: Appointment, tasks: Task[]) => {
  const { data: prefs } = usePreferences();
  const getTaskDisplayName = useGetTaskDisplayName();
  const appointmentTasks = useMemo(() => {
    return Object.entries(appointment.tasks ?? {}).reduce(
      (acc, [taskType, value]) => {
        switch (taskType) {
          case 'isBwDue':
            if (prefs?.taskBitewings !== 0 && value) {
              acc.push({
                label: getTaskDisplayName('BW_DUE'),
                lastTreatmentDate: appointment.lastTreatments.lastBw,
              });
            }
            break;
          case 'isFluorideDue':
            if (prefs?.taskFluoride !== 0 && value) {
              acc.push({
                label: getTaskDisplayName('FLUORIDE_DUE'),
                lastTreatmentDate: appointment.lastTreatments.lastFluoride,
              });
            }
            break;
          case 'isPerioChartDue':
            if (prefs?.taskPerioChart !== 0 && value) {
              acc.push({
                label: getTaskDisplayName('PERIO_CHART_DUE'),
                lastTreatmentDate: appointment.lastTreatments.lastPerioChart,
              });
            }
            break;
          case 'isPerioMaintenanceDue':
            if (prefs?.taskPerioMaintenance !== 0 && value) {
              acc.push({
                label: getTaskDisplayName('PERIO_MAINTENANCE_DUE'),
                lastTreatmentDate:
                  appointment.lastTreatments.lastPerioMaintenance,
              });
            }
            break;
          case 'isProphyDue':
            if (prefs?.taskProphy !== 0 && value) {
              acc.push({
                label: getTaskDisplayName('PROPHY_DUE'),
                lastTreatmentDate: appointment.lastTreatments.lastProphy,
              });
            }
            break;
          default:
            break;
        }

        return acc;
      },
      [] as DisplayAppointmentTask[],
    );
  }, [
    appointment.lastTreatments.lastBw,
    appointment.lastTreatments.lastFluoride,
    appointment.lastTreatments.lastPerioChart,
    appointment.lastTreatments.lastPerioMaintenance,
    appointment.lastTreatments.lastProphy,
    appointment.tasks,
    getTaskDisplayName,
    prefs?.taskBitewings,
    prefs?.taskFluoride,
    prefs?.taskPerioChart,
    prefs?.taskPerioMaintenance,
    prefs?.taskProphy,
  ]);

  const gamifiedTasks = useMemo(
    () =>
      tasks.map((task) => {
        switch (task.type) {
          case 'BW_DUE':
            return {
              task,
              lastTreatmentDate: appointment.lastTreatments.lastBw,
            };
          case 'PERIO_CHART_DUE':
            return {
              task,
              lastTreatmentDate: appointment.lastTreatments.lastPerioChart,
            };
          case 'PERIO_MAINTENANCE_DUE':
            return {
              task,
              lastTreatmentDate:
                appointment.lastTreatments.lastPerioMaintenance,
            };
          case 'PROPHY_DUE':
            return {
              task,
              lastTreatmentDate: appointment.lastTreatments.lastProphy,
            };
          default:
            return { task, lastTreatmentDate: null };
        }
      }),
    [
      appointment.lastTreatments.lastBw,
      appointment.lastTreatments.lastPerioChart,
      appointment.lastTreatments.lastPerioMaintenance,
      appointment.lastTreatments.lastProphy,
      tasks,
    ],
  );

  return { appointmentTasks, gamifiedTasks };
};
